import { useEffect, useMemo, useState } from 'react';

import {
  GridColDef,
  GridEventListener,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';

import Action from '../components/ActionButton/types/action.type';
import ActionColumn from '../components/ActionColumn';
import CellWrapper from '../components/CellWrapper';
import HeaderWrapper from '../components/HeaderWrapper';
import customFilterOperators from '../configs/customFilterOperators';

type Args = {
  initialColumns: readonly GridColDef<GridValidRowModel>[];
  rowActions?: Action[];
  checkboxSelection?: boolean;
};

const useSortableTableColumns = ({
  initialColumns,
  rowActions,
  checkboxSelection = true,
}: Args) => {
  const [columnsOrder, setColumnsOrder] = useState<Record<string, number>>({});

  const handleColumnOrderChange: GridEventListener<'columnOrderChange'> = (
    params,
  ) => {
    const { column, targetIndex, oldIndex } = params;

    setColumnsOrder((prevOrder) => {
      const newOrder = { ...prevOrder };

      if (targetIndex > oldIndex) {
        for (const key in newOrder) {
          if (newOrder[key] > oldIndex && newOrder[key] <= targetIndex) {
            newOrder[key] -= 1;
          }
        }
      } else {
        for (const key in newOrder) {
          if (newOrder[key] >= targetIndex && newOrder[key] < oldIndex) {
            newOrder[key] += 1;
          }
        }
      }

      newOrder[column.field] = targetIndex;
      return newOrder;
    });
  };

  useEffect(() => {
    setColumnsOrder((prevOrder) => {
      const newOrder = { ...prevOrder };

      /**
       * To set correct position when checkboxSelection is true we need to add related column.
       * By default checkbox column is not added to the columns list, since it is a system column.
       */
      const newInitialColumns = checkboxSelection
        ? [{ field: '__checkbox__' }, ...initialColumns]
        : initialColumns;

      newInitialColumns.forEach((col, index) => {
        if (!(col.field in newOrder)) {
          newOrder[col.field] = index;
        }
      });

      return newOrder;
    });
  }, [initialColumns, checkboxSelection]);

  const orderedColumns = useMemo(() => {
    const sortedColumns = [...initialColumns].sort((a, b) => {
      const orderA = columnsOrder[a.field] ?? Number.MAX_SAFE_INTEGER;
      const orderB = columnsOrder[b.field] ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    return sortedColumns.map((column) => {
      switch (column.field) {
        case 'actions':
          return {
            ...column,
            width: 50,
            filterable: false,
            sortable: false,
            groupable: false,
            disableReorder: true,
            disableColumnMenu: true,
            disableExport: true,
            resizable: false,
            renderHeader: () => null,
            renderCell: (params) => (
              <ActionColumn currentCellParams={params} actions={rowActions} />
            ),
          };
        default:
          return {
            ...column,
            filterOperators: customFilterOperators,
            sortable: false,
            width: column.width,
            minWidth: 200,
            flex: column.width ? undefined : 1,
            renderHeader: (params) => (
              <HeaderWrapper
                currentHeaderParams={params}
                originColumn={column}
              />
            ),
            renderCell: (params) => (
              <CellWrapper currentCellParams={params} originColumn={column} />
            ),
          };
      }
    });
  }, [initialColumns, columnsOrder, rowActions]) as GridColDef[];

  return { columns: orderedColumns, handleColumnOrderChange };
};

export default useSortableTableColumns;
